import { useEffect, useState } from 'react';
import { Undo2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';

/** An action waiting to be sent: the Overview's (spec 154), or a Remind (spec 164). */
export interface HeldCountdownAction {
  /** The words on the row: "Done", "Remind". */
  label: string;
  /** When it is sent, in epoch milliseconds. */
  sendAt: number;
  state: 'held' | 'sending' | 'sent';
}

/**
 * A held action on its row (spec 154 FR-006; shared since spec 164): what will happen, in how many
 * seconds, and Undo. Once it is being sent, what it is doing; once sent,
 * that it was.
 *
 * The seconds are for sighted readers: the live region said the whole
 * sentence once, and a number read aloud every second would drown it. The
 * bar shrinks to the moment it is sent, and is not drawn under reduced
 * motion.
 */
export function HeldCountdown({
  action,
  title,
  onUndo,
}: {
  action: HeldCountdownAction;
  /** What it is for, in Undo's accessible name: a task, a person. */
  title: string;
  onUndo: () => void;
}) {
  const left = useSecondsLeft(action.sendAt, action.state === 'held');

  if (action.state !== 'held') {
    return (
      <p
        className="text-muted-foreground text-xs"
        data-held-state={action.state}
      >
        {action.state === 'sending'
          ? `${action.label}…`
          : `${action.label} · sent`}
      </p>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1" data-held-state="held">
      <div className="flex items-center gap-2">
        <p className="text-foreground text-xs font-medium">
          {action.label}{' '}
          <span
            aria-hidden="true"
            className="text-muted-foreground tabular-nums"
          >
            in {String(left)}s
          </span>
        </p>
        <Button
          type="button"
          size="xs"
          variant="outline"
          aria-label={`Undo ${action.label}: ${title}`}
          onClick={onUndo}
          className="pointer-coarse:min-h-11"
        >
          <Undo2Icon aria-hidden="true" strokeWidth={1.5} />
          Undo
        </Button>
      </div>
      <HoldBar sendAt={action.sendAt} />
    </div>
  );
}

/** Whole seconds until `sendAt`, ticking while `running`. */
function useSecondsLeft(sendAt: number, running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 250);
    return () => {
      clearInterval(timer);
    };
  }, [running]);
  return Math.max(0, Math.ceil((sendAt - now) / 1000));
}

/** Shrinks from full to empty over the time left. */
function HoldBar({ sendAt }: { sendAt: number }) {
  const [started, setStarted] = useState(false);
  const [remaining] = useState(() => Math.max(0, sendAt - Date.now()));
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setStarted(true);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, []);
  return (
    <span
      aria-hidden="true"
      className="bg-muted relative hidden h-0.5 w-full max-w-48 overflow-hidden rounded-full motion-safe:block"
    >
      <span
        className="bg-primary absolute inset-y-0 left-0 transition-[width] ease-linear"
        style={{
          width: started ? '0%' : '100%',
          transitionDuration: `${String(remaining)}ms`,
        }}
      />
    </span>
  );
}
